import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import KoffieLayout from './KoffieLayout';
import styled, { keyframes } from 'styled-components';
import { KoffieThemeProvider } from './KoffieThemeContext';
import KoffieDashboard from './KoffieDashboard';
import KoffieSettings from './KoffieSettings';
import KoffieChangePassword from './KoffieChangePassword';
import KoffiePinSettings from './KoffiePinSettings';
import KoffieLegal from './KoffieLegal';
import KoffieTerms from './KoffieTerms';
import KoffieMachines from './KoffieMachines';
import KoffieContact from './KoffieContact';
import { useKoffiePinLock } from './useKoffiePinLock';

export const KoffieNavContext = createContext({ handleNavigation: (cb) => cb() });
export const useKoffieNav = () => useContext(KoffieNavContext);

const PageWrapper = styled.div`
  transition: opacity 0.18s ease, transform 0.18s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: ${props => props.$transitioning ? 0.6 : 1};
  transform: ${props => props.$transitioning ? 'scale(0.985)' : 'scale(1)'};
  will-change: opacity, transform;
`;

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const LockOverlay = styled.div`
  position: fixed; inset: 0; background: ${p => p.theme.colors.background};
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  z-index: 99999; animation: ${fadeIn} 0.2s ease;
`;

const LockIcon = styled.div`
  margin-bottom: 18px;
`;

const LockLogo = styled.img`
  width: 120px; height: auto; object-fit: contain;
`;

const LockTitle = styled.h2`
  font-size: 22px; font-weight: 700; color: ${p => p.theme.colors.text};
  margin: 0 0 6px; letter-spacing: -0.02em;
`;

const LockSub = styled.p`
  font-size: 14px; color: ${p => p.theme.colors.muted}; margin: 0 0 28px; opacity: 0.5;
`;

const PinDots = styled.div`display: flex; gap: 14px; margin-bottom: 28px;`;

const PinDot = styled.div`
  width: 14px; height: 14px; border-radius: 50%;
  background: ${p => p.$filled ? '#f97316' : 'transparent'};
  border: 2px solid ${p => p.$filled ? '#f97316' : p.theme.colors.border};
  transition: all 0.15s;
`;

const Numpad = styled.div`
  display: grid; grid-template-columns: repeat(3, 72px);
  grid-template-rows: repeat(4, 72px); gap: 10px;
`;

const NumKey = styled.button`
  background: ${p => p.$ghost ? 'transparent' : p.theme.colors.surface};
  border: ${p => p.$ghost ? 'none' : `1px solid ${p.theme.colors.border}`};
  border-radius: 50%; color: ${p => p.$ghost ? p.theme.colors.muted : p.theme.colors.text};
  font-size: ${p => p.$del ? '20px' : '22px'}; font-weight: 600;
  cursor: pointer; font-family: inherit;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  transition: background 0.1s, transform 0.1s;
  &:active { background: ${p => p.$ghost ? 'transparent' : p.theme.colors.border}; transform: scale(0.92); }
`;

const KeySub = styled.div`
  font-size: 9px; font-weight: 500; letter-spacing: 0.08em;
  text-transform: uppercase; opacity: 0.35;
`;

const LockError = styled.div`
  font-size: 13px; color: #ef4444; font-weight: 500; margin-top: -16px; margin-bottom: 16px; height: 20px;
`;

const LOCK_KEYS = [
  { d: '1', s: '' }, { d: '2', s: 'ABC' }, { d: '3', s: 'DEF' },
  { d: '4', s: 'GHI' }, { d: '5', s: 'JKL' }, { d: '6', s: 'MNO' },
  { d: '7', s: 'PQRS' }, { d: '8', s: 'TUV' }, { d: '9', s: 'WXYZ' },
  { d: null }, { d: '0', s: '' }, { d: 'del' },
];

function PinLockScreen({ unlock }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [lockedUntil, setLockedUntil] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) { setLockedUntil(0); setCountdown(0); setError(''); }
      else setCountdown(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const isLocked = lockedUntil > Date.now();

  const press = useCallback(async (digit) => {
    if (checking || isLocked || pin.length >= 6) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 6) {
      setChecking(true);
      setTimeout(async () => {
        const result = await unlock(next);
        if (!result || !result.ok) {
          if (result && result.lockedUntil) {
            setLockedUntil(result.lockedUntil);
            setError('Te veel pogingen');
          } else if (result && result.remaining !== undefined) {
            setError(`Onjuist — nog ${result.remaining} ${result.remaining === 1 ? 'poging' : 'pogingen'}`);
          } else {
            setError('Onjuiste pincode');
          }
          setTimeout(() => { setPin(''); setError(e => e === 'Onjuiste pincode' ? '' : e); setChecking(false); }, 600);
        }
      }, 150);
    }
  }, [pin, unlock, checking, isLocked]);

  const del = useCallback(() => { if (!isLocked) setPin(p => p.slice(0, -1)); }, [isLocked]);

  useEffect(() => {
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') press(e.key);
      if (e.key === 'Backspace') del();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [press, del]);

  return (
    <LockOverlay>
      <LockIcon><LockLogo src={`${process.env.PUBLIC_URL}/assets/Webvonk.png`} alt="Webvonk" /></LockIcon>
      <LockTitle>App vergrendeld</LockTitle>
      <LockSub>
        {isLocked
          ? `Te veel pogingen. Probeer opnieuw over ${countdown}s`
          : error || 'Voer je pincode in om door te gaan.'}
      </LockSub>
      <PinDots style={{ opacity: isLocked ? 0.3 : 1 }}>
        {Array.from({ length: 6 }).map((_, i) => <PinDot key={i} $filled={pin.length > i} />)}
      </PinDots>
      <LockError>{!isLocked && error ? error : ''}</LockError>
      <Numpad style={{ opacity: isLocked ? 0.3 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
        {LOCK_KEYS.map(({ d, s }, i) =>
          d === null ? <div key={i} /> :
          d === 'del' ? <NumKey key={i} $ghost $del onClick={del}>⌫</NumKey> :
          <NumKey key={i} onClick={() => press(d)}>{d}{s && <KeySub>{s}</KeySub>}</NumKey>
        )}
      </Numpad>
    </LockOverlay>
  );
}


const SESSION_KEY = 'koffie_session';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const WEBVONK_URL = 'https://swxfaqoftmwphbparotf.supabase.co';
const WEBVONK_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN3eGZhcW9mdG13cGhicGFyb3RmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1MjY1OTQsImV4cCI6MjA5NTEwMjU5NH0.IZZAiD-ndE_CvkuY3E44N8Sm7u1EXnsYaYnCda6R-v4';

async function refreshKoffieToken(refreshToken) {
  try {
    const res = await fetch(`${WEBVONK_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': WEBVONK_ANON_KEY },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export function getKoffieSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.session_expires_at || Date.now() > session.session_expires_at) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function saveKoffieSession(userData) {
  const session = {
    ...userData,
    session_expires_at: Date.now() + THIRTY_DAYS,
    token_expires_at: userData.expires_at ? userData.expires_at * 1000 : Date.now() + 3600_000,
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearKoffieSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('webvonk_superadmin');
}

function KoffieAppInner({ user, handleLogout, transitioning, mustChangePassword, onPasswordChanged }) {
  const { locked, loading: pinLoading, unlock } = useKoffiePinLock();

  if (!pinLoading && locked) return <PinLockScreen unlock={unlock} />;

  if (user && mustChangePassword) {
    return <KoffieChangePassword user={user} onLogout={handleLogout} forced onPasswordChanged={onPasswordChanged} />;
  }

  return (
    <PageWrapper $transitioning={transitioning}>
      <KoffieLayout onLogout={handleLogout}>
        <Routes>
          <Route path="dashboard" element={user ? <KoffieDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="settings" element={user ? <KoffieSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="settings/password" element={user ? <KoffieChangePassword user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="settings/pin" element={user ? <KoffiePinSettings /> : <Navigate to="/" replace />} />
          <Route path="settings/legal" element={user ? <KoffieLegal /> : <Navigate to="/" replace />} />
          <Route path="settings/terms" element={user ? <KoffieTerms /> : <Navigate to="/" replace />} />
          <Route path="machines" element={user ? <KoffieMachines onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="contact" element={user ? <KoffieContact onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to={user ? '/Koffiereparatie/dashboard' : '/'} replace />} />
        </Routes>
      </KoffieLayout>
    </PageWrapper>
  );
}

export default function KoffieApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const handleNavigation = useCallback((callback) => {
    setTransitioning(true);
    setTimeout(() => {
      callback();
      setTimeout(() => setTransitioning(false), 280);
    }, 100);
  }, []);

  useEffect(() => {
    const init = async () => {
      const session = getKoffieSession();
      if (!session) { setChecked(true); return; }

      // Ververs JWT als hij verlopen is (of bijna verloopt — buffer 60s)
      const tokenExpired = session.token_expires_at && Date.now() > session.token_expires_at - 60_000;
      let activeSession = session;
      if (tokenExpired && session.refresh_token) {
        const refreshed = await refreshKoffieToken(session.refresh_token);
        if (refreshed?.access_token) {
          const updated = {
            ...session,
            access_token: refreshed.access_token,
            refresh_token: refreshed.refresh_token,
            token_expires_at: refreshed.expires_at ? refreshed.expires_at * 1000 : Date.now() + 3600_000,
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
          setUser(updated);
          activeSession = updated;
        } else {
          clearKoffieSession();
          setChecked(true);
          return;
        }
      } else {
        setUser(session);
      }

      // Check must_change_password fresh from DB
      try {
        const res = await fetch(`${WEBVONK_URL}/rest/v1/Users?select=must_change_password&User_id=eq.${activeSession.id}&limit=1`, {
          headers: { 'Authorization': `Bearer ${activeSession.access_token}`, 'apikey': WEBVONK_ANON_KEY },
        });
        if (res.ok) {
          const rows = await res.json();
          if (rows?.[0]?.must_change_password) setMustChangePassword(true);
        }
      } catch {}

      setChecked(true);
    };
    init();
  }, []);

const handleLogout = () => {
    clearKoffieSession();
    setUser(null);
    navigate('/', { replace: true });
  };

  if (!checked) return null;

  return (
    <KoffieNavContext.Provider value={{ handleNavigation }}>
      <KoffieThemeProvider>
        <KoffieAppInner
          user={user}
          handleLogout={handleLogout}
          transitioning={transitioning}
          mustChangePassword={mustChangePassword}
          onPasswordChanged={() => setMustChangePassword(false)}
        />
      </KoffieThemeProvider>
    </KoffieNavContext.Provider>
  );
}
